import {
    createBaseQueries,
    transformErrorResponse,
    transformSuccessResponse,
    buildFormData,
    EXTERNAL_URL
} from '@/services';
import { createApi } from '@reduxjs/toolkit/query/react';

const { rawExternalApiBaseQuery: commonUtilBaseQuery } = createBaseQueries({
    externalURLType: EXTERNAL_URL.appRoot,
});

const CommonAPI = createApi({
    reducerPath: 'commonUtilAPI',
    baseQuery: commonUtilBaseQuery,
    tagTypes: ['ProfilePhoto'],
    endpoints: (builder) => ({

        // GET — fetch all dropdown data (programLevel, country, agency, district, taluka, institute)
        getDropdownAll: builder.query({
            query: () => ({
                url: `/util/student/dropdown/all`,
                method: 'GET',
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
        }),

        // GET — fetch student dropdown data (board, year, programLevel, agency, studentType, ...)
        getStudentDropdown: builder.query({
            query: (params) => ({
                url: `/util/student/dropdown`,
                method: 'GET',
                params,
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
        }),

        // GET — university list (paginated, but small enough to fetch in one page for a plain select)
        getUniversityDropdown: builder.query({
            query: (params) => ({
                url: `/util/student/dropdown/university`,
                method: 'GET',
                params,
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
        }),

        // GET — institute list; thousands of records, so this is only ever paged through a lazy/searchable select
        getInstituteDropdown: builder.query({
            query: (params) => ({
                url: `/util/student/dropdown/institute`,
                method: 'GET',
                params,
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
        }),

        // GET — fetch the student's profile photo (shared across portals)
        getProfilePhoto: builder.query({
            query: () => ({
                url: `/ssp/student/profile-photo`,
                method: 'GET',
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
            providesTags: ['ProfilePhoto'],
        }),

        // POST — upload/replace the student's profile photo
        uploadProfilePhoto: builder.mutation({
            query: (file) => ({
                url: `/ssp/student/profile-photo`,
                method: 'POST',
                body: buildFormData({ file }),
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
            invalidatesTags: ['ProfilePhoto'],
        }),

        // GET — public (no login) skill passport by the student's encrypted USK ID — Skill Passport QR page
        getPublicSkillPassport: builder.query({
            query: (encryptedUskId) => ({
                url: `/util/landing-page/skill-passport`,
                method: 'GET',
                params: { encryptedUskId },
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
        }),

        // GET — fetch quick services / mobile app menus for web
        getQuickServices: builder.query({
            query: () => ({
                url: `/util/quick-service/web`,
                method: 'GET',
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse,
        }),

        getAllStudentRegistrationDropdown: builder.query({
            query: () => ({
                url: "/util/student/dropdown",
                method: 'GET',
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse
        }),
        getInstitutes: builder.query({
            query: ({ page = 0, size = 10, search = '', universityId } = {}) => ({
                url: "/util/student/dropdown/institute",
                method: 'GET',
                params: {
                    page,
                    size,
                    ...(search ? { search } : {}),
                    ...(universityId ? { universityId } : {}),
                }
            }),
            transformResponse: transformSuccessResponse,
            transformErrorResponse: transformErrorResponse
        })
    }),
});

export const {
    useGetDropdownAllQuery,
    useLazyGetDropdownAllQuery,
    useGetStudentDropdownQuery,
    useLazyGetStudentDropdownQuery,
    useGetUniversityDropdownQuery,
    useLazyGetUniversityDropdownQuery,
    useLazyGetInstituteDropdownQuery,
    useGetProfilePhotoQuery,
    useLazyGetProfilePhotoQuery,
    useUploadProfilePhotoMutation,
    useGetPublicSkillPassportQuery,
    useGetQuickServicesQuery,
    useLazyGetQuickServicesQuery,
    useLazyGetAllStudentRegistrationDropdownQuery, useLazyGetInstitutesQuery
} = CommonAPI;

export default CommonAPI;
